import PropTypes from "prop-types";
import { useEffect, useMemo, useState } from "react";
import { Tag, X } from "lucide-react";
import {
  isUnqualifiedLeadStatus,
  leadStatusLabel,
  selectableLeadStatuses,
} from "@/lib/leadStatus";
import { FilterSelect } from "@/components/common/FilterSelect";
import "@/pages/signals/rnc/rncPlatform.css";

/**
 * Change lead status for an enrolled RNC.
 * Comment is required only for unqualified statuses.
 */
export function ChangeLeadStatusModal({
  open,
  onOpenChange,
  company,
  rncId,
  currentLeadStatus,
  saving = false,
  onSubmit,
}) {
  const options = useMemo(
    () => selectableLeadStatuses(currentLeadStatus),
    [currentLeadStatus],
  );
  const [leadStatus, setLeadStatus] = useState("");
  const [comment, setComment] = useState("");
  const [error, setError] = useState("");

  useEffect(() => {
    if (!open) return;
    setLeadStatus(options[0]?.value || "");
    setComment("");
    setError("");
  }, [open, options]);

  if (!open) return null;

  const needsComment = isUnqualifiedLeadStatus(leadStatus);
  const currentLabel = leadStatusLabel(currentLeadStatus) || "—";

  const handleSave = async () => {
    if (!leadStatus) {
      setError("Select a status");
      return;
    }
    if (needsComment && !comment.trim()) {
      setError("A comment is required when marking a lead as unqualified");
      return;
    }
    setError("");
    try {
      await onSubmit({
        lead_status: leadStatus,
        comment: comment.trim() || null,
      });
    } catch (e) {
      setError(e?.message || "Failed to update status");
    }
  };

  return (
    <div className="rnc-platform">
      {/* eslint-disable-next-line */}
      <div
        role="presentation"
        className="modal-overlay"
        onClick={(e) => {
          if (e.target === e.currentTarget && !saving) onOpenChange(false);
        }}
        onKeyDown={(e) => {
          if (e.key === "Escape" && !saving) onOpenChange(false);
        }}
      >
        <dialog
          open
          style={{ display: "contents" }}
          aria-labelledby="change-lead-status-title"
        >
          <div className="modal-box" data-testid="change-lead-status-modal" style={{ maxWidth: 480 }}>
            <div className="modal-header">
            <div>
              <h2 id="change-lead-status-title">
                <Tag size={16} /> Change lead status
              </h2>
              <p>
                {company || "Lead"}
                {rncId ? ` · ${rncId}` : ""}
                {" · "}
                Current: {currentLabel}
              </p>
            </div>
            <button
              type="button"
              className="modal-close"
              onClick={() => !saving && onOpenChange(false)}
              aria-label="Close"
            >
              <X size={14} />
            </button>
          </div>

          <div className="modal-body" style={{ display: "flex", flexDirection: "column", gap: 14 }}>
            {!options.length ? (
              <p style={{ margin: 0, color: "var(--slate-500)", fontSize: 13 }}>
                No status changes are available from the current state.
              </p>
            ) : (
              <>
                <div className="field">
                  <label htmlFor="lead-status-select">STATUS</label>
                  <FilterSelect
                    id="lead-status-select"
                    variant="field"
                    value={leadStatus}
                    onChange={(next) => {
                      setLeadStatus(next);
                      setError("");
                    }}
                    options={options.map((o) => ({ value: o.value, label: o.label }))}
                    disabled={saving}
                    data-testid="lead-status-select"
                    aria-label="Lead status"
                    className="mt-1"
                  />
                  {leadStatus && (
                    <p style={{ margin: "6px 0 0", fontSize: 12, color: "var(--slate-500)" }}>
                      {options.find((o) => o.value === leadStatus)?.description}
                    </p>
                  )}
                </div>

                <div className="field">
                  <label htmlFor="lead-status-comment">
                    COMMENT
                    {needsComment ? " (required)" : " (optional)"}
                  </label>
                  <textarea
                    id="lead-status-comment"
                    value={comment}
                    onChange={(e) => {
                      setComment(e.target.value);
                      setError("");
                    }}
                    disabled={saving}
                    rows={4}
                    placeholder={
                      needsComment
                        ? "Explain why this lead is unqualified…"
                        : "Add a note for the sales team (optional)"
                    }
                    data-testid="lead-status-comment"
                    style={{
                      width: "100%",
                      padding: "8px 10px",
                      borderRadius: 8,
                      border: "1px solid var(--slate-200)",
                      fontSize: 13,
                      resize: "vertical",
                      fontFamily: "inherit",
                    }}
                  />
                </div>
              </>
            )}

            {error ? (
              <p style={{ margin: 0, color: "#b91c1c", fontSize: 13 }} data-testid="lead-status-error">
                {error}
              </p>
            ) : null}
          </div>

          <div className="modal-footer">
            <button type="button" className="btn" disabled={saving} onClick={() => onOpenChange(false)}>
              Cancel
            </button>
            <button
              type="button"
              className="btn primary"
              disabled={saving || !options.length}
              onClick={handleSave}
              data-testid="lead-status-save"
            >
              {saving ? "Saving…" : "Update status"}
            </button>
          </div>
        </div>
      </dialog>
      </div>
    </div>
  );
}

ChangeLeadStatusModal.propTypes = {
  open: PropTypes.bool.isRequired,
  onOpenChange: PropTypes.func.isRequired,
  company: PropTypes.string,
  rncId: PropTypes.string,
  currentLeadStatus: PropTypes.string,
  saving: PropTypes.bool,
  onSubmit: PropTypes.func.isRequired,
};
